/**
 * Documentos da Proposta para o CLIENTE (DEMO-03, telas/Documentos.tsx):
 * Proposta Comercial (Venda), Proposta de Locação e Proposta de Trade-in.
 * Preços de venda, totais ao Cliente, condições e faturamento; NUNCA custo,
 * margem/comissão ou cálculos internos. O documento é a própria folha,
 * impressa/salva em PDF pelo navegador.
 * Dados da INTTRIX (P-18): parâmetro `comercial.empresa`; sem ele, o documento
 * sai como PRÉVIA. Na versão enviada, termos e dados da INTTRIX são os
 * congelados no envio.
 */
import type { ReactNode } from 'react';
import { useAmbiente } from '../../ambiente.tsx';
import { CAMPOS_CONDICOES_LOCACAO, textoReajuste } from '../../../../backend/src/compartilhado/comercial.ts';
import { textosCobrancaCliente } from '../../../../backend/src/compartilhado/locacao.ts';
import { dataBr } from '../../../../backend/src/compartilhado/oportunidades.ts';
import {
  SITUACOES_VERSAO,
  avaliacaoSelecionada,
  calculoLocacao,
  calculoTradein,
  contatoDe,
  economiaVersao,
  entidadeClienteDe,
  entidadeDe,
  enderecoTexto,
  faturadores,
  fimEstimado,
  locaisDaVersao,
  localParqueDaVersao,
  nomeComponente,
  ofertaSelecionada,
  pagamentoTexto,
  produtoDoItem,
  reajusteDaLocacao,
  tipoDaVersao,
  type DadosEmpresa,
  type LocalCongelado,
  type TermoComercial,
  type TermosGerais,
  type Versao,
  RECEBIMENTO_TRADEIN,
  recebimentoTradein,
} from '../../../../backend/src/compartilhado/propostas.ts';
import { useProposta } from './contexto.tsx';
import { Bloco, DocumentoInttrix, Icone, Linhas, linhasEndereco, mesmoEndereco } from '../documentos/DocumentoInttrix.tsx';
import { moeda2 } from './formatos.ts';

const localTexto = (l: LocalCongelado) => `${l.razao} — CNPJ ${l.cnpj} — ${enderecoTexto(l.endereco)}`;

/** Dados da INTTRIX e termos do documento: os congelados na versão enviada; no rascunho, os vigentes. */
function useDocumento(v: Versao): { empresa: DadosEmpresa | null; termos: TermoComercial[] } {
  const { dados } = useProposta();
  if (v.situacao !== 'rascunho') return { empresa: v.empresa ?? null, termos: v.termos ?? [] };
  return { empresa: dados.opcoes.empresa, termos: dados.opcoes.termos_venda };
}

/**
 * Termos Gerais (só Proposta de Venda): no rascunho, os vigentes; na versão enviada, SÓ a cópia congelada no envio —
 * versões enviadas antes da existência dos Termos Gerais não recebem o conteúdo retroativamente.
 */
function useTermosGerais(v: Versao): TermosGerais | null {
  const { dados } = useProposta();
  if (v.situacao !== 'rascunho') return v.termosGerais ?? null;
  return dados.opcoes.termos_gerais ?? null;
}

export function Folha({ titulo, v, voltar, teste, children }: { titulo: string; v: Versao; voltar: string; teste: string; children: ReactNode }) {
  const { empresa } = useDocumento(v);
  return (
    <FolhaDocumento titulo={titulo} empresa={empresa} rascunho={v.situacao === 'rascunho' ? 'RASCUNHO — NÃO ENVIADO' : undefined} avisoRascunho="Rascunho: o documento definitivo é o da versão enviada." voltar={voltar} teste={teste}>
      {children}
    </FolhaDocumento>
  );
}

/**
 * Folha de documento (Proposta, OCF, OL/OTI): logo, dados da INTTRIX (os congelados no documento), marca-d'água de
 * PRÉVIA sem os dados da INTTRIX, de rascunho quando houver, e de AMBIENTE DE TESTE fora da produção.
 */
export function FolhaDocumento({ titulo, subtitulo, empresa, rascunho, avisoRascunho, voltar, teste, children }: { titulo: string; subtitulo?: string; empresa: DadosEmpresa | null; rascunho?: string; avisoRascunho?: string; voltar: string; teste: string; children: ReactNode }) {
  const { faixa } = useAmbiente();
  const marca = !empresa ? 'PRÉVIA — DADOS DA INTTRIX NÃO CONFIGURADOS' : rascunho ? rascunho : faixa ? 'AMBIENTE DE TESTE — DADOS FICTÍCIOS' : '';
  const aviso = !empresa ? 'Os dados da INTTRIX nos documentos (razão social, CNPJ, endereço e contatos) aguardam cadastro em Parâmetros (P-18): este documento é só uma prévia.' : rascunho ? (avisoRascunho ?? '') : '';
  return (
    <>
      <div className="barra-doc">
        <a className="botao-secundario" href={`#${voltar}`}>
          ← Voltar
        </a>
        <button type="button" className="botao-primario" onClick={() => window.print()} data-teste="imprimir-pdf">
          Imprimir / salvar como PDF
        </button>
        {aviso && <span className="texto-secundario">{aviso}</span>}
      </div>
      <article className="folha" data-teste={teste} aria-label={titulo}>
        {marca && (
          <div className="marca-dagua" aria-hidden data-teste="marca-dagua">
            {marca}
          </div>
        )}
        <header className="folha-cabecalho">
          <div className="folha-marca">
            <img src="marca/logo-inttrix.jpg" alt="INTTRIX Technology" width="224" height="70" data-teste="logo-documento" />
          </div>
          <div>
            <h1>{titulo}</h1>
            {subtitulo && <p className="folha-subtitulo">{subtitulo}</p>}
            <p data-teste="dados-inttrix">{empresa ? [`${empresa.razao} | CNPJ ${empresa.cnpj}`, empresa.endereco, [empresa.telefone, empresa.email, empresa.site].filter(Boolean).join(' · ')].filter(Boolean).join(' — ') : 'Dados da INTTRIX a configurar (P-18)'}</p>
          </div>
        </header>
        {children}
        {aviso && <footer className="folha-rodape">{aviso}</footer>}
      </article>
    </>
  );
}

export function Secao({ titulo, children, teste }: { titulo: string; children: ReactNode; teste?: string }) {
  return (
    <section className="folha-secao" data-teste={teste}>
      <h2>{titulo}</h2>
      {children}
    </section>
  );
}

export function Campos({ pares }: { pares: [string, ReactNode][] }) {
  return (
    <dl className="folha-campos">
      {pares.map(([k, x]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{x || '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Responsável INTTRIX da versão (nome e e-mail do usuário). */
function useResponsavel(v: Versao) {
  const { dados } = useProposta();
  const r = dados.opcoes.responsaveis.find((x) => x.id === v.responsavel);
  return { nome: r?.nome ?? '—', email: r?.email ?? '' };
}

/* ---------- Proposta Comercial (para o CLIENTE) ---------- */
export function PropostaPdf({ numero }: { numero: number }) {
  const { d, dados } = useProposta();
  const p = dados.proposta;
  const v = p?.versoes.find((x) => x.numero === numero);
  if (!p || !v) return <p className="vazio">Proposta não encontrada.</p>;
  const voltar = `/oportunidades/${dados.oportunidade.id}/propostas/${p.id}/v/${v.numero}`;
  const tipo = tipoDaVersao(v);
  if (tipo === 'locacao') return <PdfLocacao v={v} numero={p.numero} voltar={voltar} />;
  if (tipo === 'trade_in') return <PdfTradein v={v} numero={p.numero} voltar={voltar} />;
  return <PdfVenda v={v} numero={p.numero} voltar={voltar} d={d} />;
}

/** Frete na Proposta: CIF sem frase redundante; FOB conforme quem paga (08/10/2026); FOB absorvido = "incluso no preço" (C14). */
function textoFrete(f: { modalidade: string; pagador: string }) {
  if (f.modalidade === 'CIF') return 'CIF (incluso no preço)';
  return f.pagador === 'cliente' ? 'FOB (pago pelo cliente)' : 'FOB (incluso no preço)';
}

/** Situação da versão no cabeçalho: verde para as situações positivas, neutro nas demais. */
const POSITIVAS = new Set(['enviada', 'em_negociacao', 'aceita', 'aceita_parcialmente']);

/* ---------- Proposta Comercial de VENDA (para o CLIENTE) — layout aprovado em 08/10/2026 ---------- */
function PdfVenda({ v, numero, voltar, d }: { v: Versao; numero: string; voltar: string; d: ReturnType<typeof useProposta>['d'] }) {
  const ent = entidadeClienteDe(d, v.entidadeClienteId)?.entidade;
  const ac = contatoDe(d, v.contatoId);
  const locais = locaisDaVersao(d, v);
  const eco = economiaVersao(d, v);
  const resp = useResponsavel(v);
  const { empresa } = useDocumento(v);
  const termosGerais = useTermosGerais(v);
  const fs = faturadores(d, v);
  const fat = locais.faturamento;
  const ent2 = locais.entrega;
  // mesmo endereço: a Proposta diz que é o do faturamento, ou o local de entrega coincide com ele (CNPJ e endereço)
  const mesmoLocal = v.entrega.mesmoFaturamento || (ent2.cnpj === fat.cnpj && mesmoEndereco(ent2.endereco, fat.endereco));
  const contatoEntrega = [ent2.contato, ent2.telefone].filter(Boolean).join(' — ');
  return (
    <DocumentoInttrix
      titulo="PROPOSTA COMERCIAL"
      colunasIdent={3}
      ident={[
        ['Proposta', numero],
        ['Versão', String(v.numero)],
        ['Data', dataBr(v.enviadaEm ?? v.data)],
        ['Validade', dataBr(v.validade)],
        ['Situação', <span className={`d-selo${POSITIVAS.has(v.situacao) ? '' : ' d-neutro'}`}>{SITUACOES_VERSAO[v.situacao]}</span>],
      ]}
      empresa={empresa}
      rascunho={v.situacao === 'rascunho' ? 'RASCUNHO — NÃO ENVIADO' : undefined}
      avisoRascunho="Rascunho: o documento definitivo é o da versão enviada."
      voltar={voltar}
      teste="pdf-proposta"
    >
      <Bloco n={1} titulo="CLIENTE" teste="pdf-cliente-proposta">
        <div className="d-corpo">
          <div className="d-v d-forte" style={{ fontSize: '11.5px', margin: '0 0 6px 25px' }}>{ent ? `${ent.razao} (${ent.tipo})` : '—'}</div>
          <div className="d-grade d-div-v" style={{ gridTemplateColumns: '1.1fr 1.1fr 1fr 1.3fr .9fr' }}>
            <div className="d-par"><Icone nome="predio" /><div><div className="d-r">CNPJ</div><div className="d-v d-sem-quebra">{ent?.cnpj || '—'}</div></div></div>
            <div className="d-par"><Icone nome="documento" /><div><div className="d-r">Inscrição estadual</div><div className="d-v">{ent?.ie || '—'} {ent?.ieSituacao && <span className={`d-selo${/ativ/i.test(ent.ieSituacao) ? '' : ' d-neutro'}`}>{ent.ieSituacao}</span>}</div></div></div>
            <div className="d-par"><Icone nome="pessoa" /><div><div className="d-r">A/C</div><div className="d-v">{ac?.nome || '—'}</div></div></div>
            <div className="d-par"><Icone nome="email" /><div><div className="d-r">E-mail</div><div className="d-v">{ac?.email || '—'}</div></div></div>
            <div className="d-par"><Icone nome="telefone" /><div><div className="d-r">Telefone</div><div className="d-v d-sem-quebra">{ac?.telefone || '—'}</div></div></div>
          </div>
        </div>
      </Bloco>
      <Bloco n={2} titulo="RESPONSÁVEL PELA PROPOSTA" teste="pdf-responsavel">
        <div className="d-corpo d-grade d-div-v" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <div className="d-par" style={{ alignItems: 'center' }}><Icone nome="pessoa" grande /><div className="d-v d-forte" style={{ fontSize: '11px' }}>{resp.nome}</div></div>
          <div className="d-par" style={{ alignItems: 'center' }}><Icone nome="email" /><span className="d-v">{resp.email || '—'}</span></div>
        </div>
      </Bloco>
      <Bloco n={3} titulo="INVESTIMENTO" teste="pdf-investimento" quebra>
        <div className="d-corpo d-tabela">
          <table className="d-tab">
            <thead>
              <tr>
                <th className="d-centro" style={{ width: 34 }}>Item</th>
                <th style={{ width: 118 }}>PN/SKU</th>
                <th>Descrição</th>
                <th className="d-centro" style={{ width: 34 }}>Qtd.</th>
                <th className="d-centro" style={{ width: 38 }}>Unid.</th>
                <th className="d-centro" style={{ width: 44 }}>Moeda</th>
                <th className="d-num" style={{ width: 82 }}>Venda unit.</th>
                <th className="d-num" style={{ width: 86 }}>Total linha</th>
              </tr>
            </thead>
            <tbody>
              {v.itens.map((i, n) => {
                const pr = produtoDoItem(d, i);
                return (
                  <tr key={i.id}>
                    <td className="d-centro">{n + 1}</td>
                    <td className="d-pn">{pr.sku}</td>
                    <td>{pr.nome}</td>
                    <td className="d-centro">{i.quantidade}</td>
                    <td className="d-centro">{pr.unidade || '—'}</td>
                    <td className="d-centro">R$</td>
                    <td className="d-num">{moeda2(i.precoUnit)}</td>
                    <td className="d-num">{moeda2(i.precoUnit * i.quantidade)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {eco.freteAoCliente > 0 && (
            <p className="d-subtotais" data-teste="pdf-total">
              Total dos produtos {moeda2(eco.totalProdutos)} · Frete (FOB, pago pelo cliente) {moeda2(eco.freteAoCliente)}
            </p>
          )}
          <div className="d-total" data-teste="pdf-total-final">
            <span className="d-rot">TOTAL DA PROPOSTA</span>
            <span className="d-val">{moeda2(eco.totalFinal)}</span>
          </div>
        </div>
      </Bloco>
      <Bloco n={4} titulo="CONDIÇÕES COMERCIAIS" teste="pdf-condicoes">
        <div className="d-corpo">
          <div className="d-cards">
            <div className="d-par"><Icone nome="calendario" /><div><div className="d-v d-forte">Pagamento</div><div className="d-v">{pagamentoTexto(v.condicoes)}</div></div></div>
            <div className="d-par"><Icone nome="caminhao" /><div><div className="d-v d-forte">Frete</div><div className="d-v">{textoFrete(eco.frete)}</div></div></div>
            <div className="d-par"><Icone nome="relogio" /><div><div className="d-v d-forte">Prazo de entrega</div><div className="d-v">{v.condicoes.prazoGeral || '—'}</div></div></div>
          </div>
          {v.condicoes.observacao?.trim() && (
            <div className="d-obs d-par"><Icone nome="nota" /><div><div className="d-r">Observação</div><div className="d-v">{v.condicoes.observacao}</div></div></div>
          )}
        </div>
      </Bloco>
      <Bloco n={5} titulo="FATURAMENTO E ENTREGA" extra={mesmoLocal ? undefined : '(ENDEREÇOS DIFERENTES)'} teste="pdf-faturamento-entrega">
        {mesmoLocal ? (
          <div className="d-corpo d-grade d-div-v" style={{ gridTemplateColumns: '1.5fr 1fr' }}>
            <div className="d-par"><Icone nome="predio" grande /><div><div className="d-r d-forte">Faturar e entregar em</div><div className="d-v d-forte">{fat.razao}</div><div className="d-v">CNPJ {fat.cnpj || '—'}</div><Linhas linhas={linhasEndereco(fat.endereco)} /></div></div>
            <div className="d-par"><Icone nome="telefone" grande /><div><div className="d-r d-forte">Contato de entrega</div><div className="d-v">{ent2.contato || '—'}</div>{ent2.telefone && <div className="d-v">{ent2.telefone}</div>}</div></div>
          </div>
        ) : (
          <div className="d-corpo d-grade d-div-v" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="d-par"><Icone nome="predio" grande /><div><div className="d-r d-forte">Faturar para</div><div className="d-v d-forte">{fat.razao}</div><div className="d-v">CNPJ {fat.cnpj || '—'}</div><Linhas linhas={linhasEndereco(fat.endereco)} /></div></div>
            <div>
              <div className="d-par"><Icone nome="caminhao" grande /><div><div className="d-r d-forte">Entregar em</div><div className="d-v d-forte">{ent2.razao}</div><div className="d-v">CNPJ {ent2.cnpj || '—'}</div><Linhas linhas={linhasEndereco(ent2.endereco)} /></div></div>
              <div className="d-par" style={{ marginTop: 7 }}><span className="d-ico-col"><Icone nome="telefone" /></span><div><div className="d-r d-forte">Contato de entrega</div><div className="d-v">{contatoEntrega || '—'}</div></div></div>
            </div>
          </div>
        )}
      </Bloco>
      <Bloco n={6} titulo="DADOS DE FATURAMENTO (PARCEIROS COMERCIAIS)" teste="pdf-faturamento">
        <div className="d-corpo d-tabela">
          <table className="d-tab">
            <thead>
              <tr>
                <th>Parceiro comercial</th>
                <th style={{ width: 150 }}>CNPJ faturador</th>
                <th style={{ width: 220 }}>Itens faturados</th>
              </tr>
            </thead>
            <tbody>
              {fs.map((f) => (
                <tr key={f.entidade.id}>
                  <td>{f.grupo.nome}</td>
                  <td className="d-sem-quebra">{f.entidade.cnpj}</td>
                  <td>{f.itens.map((n) => `Item ${n}`).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Bloco>
      {/* "TERMOS COMERCIAIS DA VENDA" não é exibida no PDF (decisão da Valéria, 07/10/2026); o parâmetro comercial.termos_venda continua no sistema e no envio */}
      {termosGerais && (
        <Bloco n={7} titulo="TERMOS GERAIS" teste="pdf-termos-gerais" quebra>
          {termosGerais.atencao && (
            <p className="d-atencao" data-teste="pdf-termos-gerais-atencao">
              <strong>Atenção:</strong> {termosGerais.atencao}
            </p>
          )}
          <div className="d-termos">
            {termosGerais.itens.map((t, i) => (
              <div key={i} data-teste={`pdf-termos-gerais-item-${i + 1}`}>
                {typeof t === 'string' ? (
                  <p className="folha-termos-gerais-texto">{t}</p>
                ) : (
                  <>
                    <h3 className="folha-termos-gerais-titulo">{t.titulo}</h3>
                    <p className="folha-termos-gerais-texto">{t.texto}</p>
                  </>
                )}
              </div>
            ))}
          </div>
        </Bloco>
      )}
    </DocumentoInttrix>
  );
}

/* ---------- Proposta de LOCAÇÃO (para o CLIENTE) — pacote 3, V06: cliente, responsável, locadora, itens, resumo financeiro e
 * condições contratuais; sem repetir número/data/validade nem mensalidade/prazo/cobranças; sem Modelo; sem datas antecipadas;
 * sem o acumulado do contrato (regra aprovada). Textos comerciais vêm dos dados da versão. */
function PdfLocacao({ v, numero, voltar }: { v: Versao; numero: string; voltar: string }) {
  const { d } = useProposta();
  const L = v.locacao!;
  const ent = entidadeClienteDe(d, v.entidadeClienteId)?.entidade;
  const ac = contatoDe(d, v.contatoId);
  const locais = locaisDaVersao(d, v);
  const resp = useResponsavel(v);
  const { empresa } = useDocumento(v);
  const of = ofertaSelecionada(v);
  const c = calculoLocacao(v);
  const parceiro = of ? entidadeDe(d, of.entidadeId) : undefined;
  const cond = L.condicoes;
  const cobranca = textosCobrancaCliente(cond?.regraCobranca);
  const contratuais = CAMPOS_CONDICOES_LOCACAO.filter((x) => x.chave !== 'reajuste' && x.chave !== 'implantacao' && cond?.[x.chave]);
  const local = locais.entrega;
  let n = 5;
  return (
    <DocumentoInttrix
      titulo="PROPOSTA DE LOCAÇÃO"
      colunasIdent={3}
      ident={[
        ['Proposta', numero],
        ['Versão', String(v.numero)],
        ['Data', dataBr(v.enviadaEm ?? v.data)],
        ['Validade', dataBr(v.validade)],
        ['Situação', <span className={`d-selo${POSITIVAS.has(v.situacao) ? '' : ' d-neutro'}`}>{SITUACOES_VERSAO[v.situacao]}</span>],
      ]}
      empresa={empresa}
      rascunho={v.situacao === 'rascunho' ? 'RASCUNHO — NÃO ENVIADO' : undefined}
      avisoRascunho="Rascunho: o documento definitivo é o da versão enviada."
      voltar={voltar}
      teste="pdf-proposta"
    >
      <Bloco n={1} titulo="CLIENTE" teste="pdf-cliente-proposta">
        <div className="d-corpo">
          <div className="d-v d-forte" style={{ fontSize: '11.5px', margin: '0 0 6px 25px' }}>{ent ? `${ent.razao} (${ent.tipo})` : '—'}</div>
          <div className="d-grade d-div-v" style={{ gridTemplateColumns: '1.1fr 1fr 1.3fr .9fr' }}>
            <div className="d-par"><Icone nome="predio" /><div><div className="d-r">CNPJ</div><div className="d-v d-sem-quebra">{ent?.cnpj || '—'}</div></div></div>
            <div className="d-par"><Icone nome="pessoa" /><div><div className="d-r">Contato</div><div className="d-v">{ac?.nome || '—'}</div></div></div>
            <div className="d-par"><Icone nome="email" /><div><div className="d-r">E-mail</div><div className="d-v">{ac?.email || '—'}</div></div></div>
            <div className="d-par"><Icone nome="telefone" /><div><div className="d-r">Telefone</div><div className="d-v d-sem-quebra">{ac?.telefone || '—'}</div></div></div>
          </div>
        </div>
      </Bloco>
      <div className="d-grade" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Bloco n={2} titulo="RESPONSÁVEL PELA PROPOSTA" teste="pdf-responsavel">
          <div className="d-corpo">
            <div className="d-par"><Icone nome="pessoa" /><div><div className="d-r">Responsável</div><div className="d-v d-forte">{resp.nome}</div></div></div>
            <div className="d-par" style={{ marginTop: 6 }}><Icone nome="email" /><div><div className="d-r">E-mail</div><div className="d-v">{resp.email || '—'}</div></div></div>
          </div>
        </Bloco>
        <Bloco n={3} titulo="PARCEIRO DE LOCAÇÃO SELECIONADO" teste="pdf-parceiro-locacao">
          <div className="d-corpo">
            <div className="d-par"><Icone nome="predio" /><div><div className="d-r">Parceiro de Locação</div><div className="d-v d-forte">{parceiro ? parceiro.razao : 'não selecionado'}</div></div></div>
            <div className="d-par" style={{ marginTop: 6 }}><Icone nome="documento" /><div><div className="d-r">CNPJ</div><div className="d-v d-sem-quebra">{parceiro?.cnpj ?? '—'}</div></div></div>
          </div>
        </Bloco>
      </div>
      <Bloco n={4} titulo="ITENS E COMPONENTES DO PROJETO" teste="pdf-itens-locacao" quebra>
        <div className="d-corpo d-tabela">
          {L.descricao && <p className="d-v" style={{ margin: '0 0 6px' }}>{L.descricao}</p>}
          <table className="d-tab">
            <thead>
              <tr>
                <th style={{ width: 118 }}>PN/SKU</th>
                <th>Descrição</th>
                <th style={{ width: 90 }}>Fabricante</th>
                <th style={{ width: 96 }}>Pilar</th>
                <th style={{ width: 70 }}>Tipo</th>
                <th className="d-centro" style={{ width: 36 }}>Qtd.</th>
              </tr>
            </thead>
            <tbody>
              {L.componentes.map((x) => (
                <tr key={x.id}>
                  <td className="d-pn">{x.pn || '—'}</td>
                  <td>{x.descricao}</td>
                  <td>{x.fabricante || '—'}</td>
                  <td>{x.pilar || '—'}</td>
                  <td>{x.tipoItem || '—'}</td>
                  <td className="d-centro">{x.quantidade}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Bloco>
      <Bloco n={5} titulo="RESUMO FINANCEIRO DA LOCAÇÃO" teste="pdf-financeiro-locacao">
        <div className="d-corpo">
          <div className="d-cards" data-teste="pdf-parcela-locacao">
            <div className="d-par"><Icone nome="calendario" /><div><div className="d-v d-forte">{c ? moeda2(c.clienteParcela) : '—'}</div><div className="d-r">{of?.periodicidade === 'Mensal' ? 'por mês' : `por parcela (${of?.periodicidade?.toLowerCase() ?? '—'})`}</div></div></div>
            <div className="d-par"><Icone nome="relogio" /><div><div className="d-v d-forte">{of ? `${of.prazoMeses} meses` : '—'}</div><div className="d-r">prazo contratual</div></div></div>
            <div className="d-par"><Icone nome="documento" /><div><div className="d-v d-forte">{c ? `${c.cobrancas} parcela(s)` : '—'}</div><div className="d-r">quantidade</div></div></div>
            {L.formaPagamento && <div className="d-par"><Icone nome="nota" /><div><div className="d-v d-forte">{L.formaPagamento}</div><div className="d-r">forma de pagamento</div></div></div>}
            {/* versões anteriores ao pacote 3 aparecem como foram emitidas (datas preenchidas no rascunho) */}
            {L.vencimentoInicial && <div className="d-par"><Icone nome="calendario" /><div><div className="d-v d-forte">{dataBr(L.vencimentoInicial)}</div><div className="d-r">vencimento inicial</div></div></div>}
          </div>
        </div>
      </Bloco>
      <Bloco n={++n} titulo="INÍCIO DA LOCAÇÃO E COBRANÇA" teste="pdf-inicio-cobranca" quebra>
        <div className="d-corpo">
          {cobranca.map((x) => (
            <div key={x.titulo} style={{ marginBottom: 6 }}>
              <div className="d-v d-forte">{x.titulo}</div>
              <div className="d-v">{x.texto}</div>
            </div>
          ))}
        </div>
      </Bloco>
      <Bloco n={++n} titulo="REAJUSTE" teste="pdf-reajuste-locacao">
        <div className="d-corpo"><div className="d-v">{textoReajuste(reajusteDaLocacao(L))}</div></div>
      </Bloco>
      <Bloco n={++n} titulo="IMPLANTAÇÃO" teste="pdf-implantacao-locacao">
        <div className="d-corpo d-grade" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <div className="d-par"><div><div className="d-r">Prazo previsto</div><div className="d-v">{L.implantacao || '—'}</div></div></div>
            <div className="d-par"><div><div className="d-r">Data prevista de implantação</div><div className="d-v">{L.dataImplantacao ? dataBr(L.dataImplantacao) : '—'}</div></div></div>
            <div className="d-par"><div><div className="d-r">Responsável pela entrega</div><div className="d-v">{L.responsavelEntrega || '—'}</div></div></div>
            <div className="d-par"><div><div className="d-r">Responsável pela implantação</div><div className="d-v">{L.responsavelImplantacao || '—'}</div></div></div>
          </div>
          <div><div className="d-r">Condição</div><div className="d-v">{cond?.implantacao || '—'}</div></div>
        </div>
      </Bloco>
      <Bloco n={++n} titulo="ENDEREÇO E CONTATO" teste="pdf-endereco-locacao">
        <div className="d-corpo d-grade" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
          <div><div className="d-r d-forte">Local de instalação</div><div className="d-v">{local.razao}</div><div className="d-v">CNPJ {local.cnpj || '—'}</div><Linhas linhas={linhasEndereco(local.endereco)} /></div>
          <div><div className="d-r d-forte">Contato no local</div><div className="d-v">{local.contato || '—'}</div><div className="d-v">{local.telefone || ''}</div></div>
        </div>
      </Bloco>
      {contratuais.map((x) => (
        <Bloco key={x.chave} n={++n} titulo={x.rotulo.toUpperCase()} teste={`pdf-condicao-${x.chave}`}>
          <div className="d-corpo"><div className="d-v">{cond![x.chave]}</div></div>
        </Bloco>
      ))}
      {L.observacoes && (
        <Bloco n={++n} titulo="OBSERVAÇÕES" teste="pdf-observacoes-locacao">
          <div className="d-corpo"><div className="d-v">{L.observacoes}</div></div>
        </Bloco>
      )}
    </DocumentoInttrix>
  );
}

/* ---------- Proposta de TRADE-IN (para o CLIENTE): sem avaliação do comprador nem valores internos ---------- */
function PdfTradein({ v, numero, voltar }: { v: Versao; numero: string; voltar: string }) {
  const { d } = useProposta();
  const T = v.tradein!;
  const ent = entidadeClienteDe(d, v.entidadeClienteId)?.entidade;
  const ac = contatoDe(d, v.contatoId);
  const tri = calculoTradein(v);
  const sel = avaliacaoSelecionada(v);
  const comprador = sel ? entidadeDe(d, sel.entidadeId) : undefined;
  const local = localParqueDaVersao(d, v);
  return (
    <Folha titulo="PROPOSTA DE TRADE-IN" v={v} voltar={voltar} teste="pdf-proposta">
      <Secao titulo="1. CLIENTE E PROPOSTA" teste="pdf-cliente-proposta">
        <Campos pares={[['Proposta', `${numero} | V${v.numero}`], ['Data', dataBr(v.enviadaEm ?? v.data)], ['Situação', SITUACOES_VERSAO[v.situacao]], ['Cliente (entidade legal)', ent ? `${ent.razao} (${ent.tipo})` : ''], ['CNPJ', ent?.cnpj ?? ''], ['A/C', ac?.nome ?? '']]} />
      </Secao>
      <Secao titulo="2. PARCEIRO COMPRADOR SELECIONADO" teste="pdf-comprador">
        <Campos pares={[['Comprador de Trade-in', comprador?.razao ?? 'não selecionado'], ['CNPJ', comprador?.cnpj ?? '—']]} />
      </Secao>
      <Secao titulo="3. INVENTÁRIO DETALHADO" teste="pdf-inventario">
        <table className="folha-tabela folha-tabela-larga">
          <thead>
            <tr>
              <th>Categoria</th>
              <th>Fabricante</th>
              <th>Modelo</th>
              <th>Part number</th>
              <th>Serial/lote</th>
              <th>Processador</th>
              <th>HD</th>
              <th>Storage</th>
              <th>Memória</th>
              <th>Tela</th>
              <th>Funcional</th>
              <th>Acessórios</th>
              <th>Observações</th>
            </tr>
          </thead>
          <tbody>
            {T.equipamentos.map((e) => (
              <tr key={e.id}>
                <td>{e.categoria}</td>
                <td>{e.fabricante}</td>
                <td>{e.modelo}</td>
                <td>{e.pn || '—'}</td>
                <td>{e.serial || e.lote || '—'}</td>
                <td>{e.processador || '—'}</td>
                <td>{e.hd || '—'}</td>
                <td>{e.storage || '—'}</td>
                <td>{e.memoria || '—'}</td>
                <td>{e.tela || '—'}</td>
                <td>{e.funcional || '—'}</td>
                <td>{e.acessorios || '—'}</td>
                <td>{e.observacoes || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Secao>
      <Secao titulo="4 a 6. QUANTIDADES, VALORES UNITÁRIOS AO CLIENTE E TOTAIS POR LINHA" teste="pdf-valores-tradein">
        <table className="folha-tabela">
          <thead>
            <tr>
              <th>Equipamento</th>
              <th>Qtd.</th>
              <th>Valor unitário ao Cliente</th>
              <th>Total da linha</th>
            </tr>
          </thead>
          <tbody>
            {T.equipamentos.map((e) => {
              const l = tri?.linhas.find((x) => x.id === e.id);
              return (
                <tr key={e.id}>
                  <td>{nomeComponente(e)}</td>
                  <td>{e.quantidade}</td>
                  <td>{moeda2(l?.unitCliente ?? 0)}</td>
                  <td>{moeda2(l?.totalCliente ?? 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Secao>
      <Secao titulo="7. TOTAL DESTINADO AO CLIENTE" teste="pdf-total-tradein">
        <p className="folha-total" data-teste="pdf-oferta-tradein">
          VALOR TOTAL DESTINADO AO CLIENTE PELA AQUISIÇÃO DO PARQUE <strong>{moeda2(tri?.oferta ?? 0)}</strong>
        </p>
      </Secao>
      <Secao titulo="8. VALIDADE" teste="pdf-validade-tradein">
        <Campos pares={[['Validade da avaliação', T.validadeAvaliacao ? dataBr(T.validadeAvaliacao) : '—']]} />
      </Secao>
      <Secao titulo="9. LOGÍSTICA" teste="pdf-logistica-tradein">
        <Campos pares={[['Modalidade', T.modalidadeLogistica || 'a definir'], ['Responsável pela logística', T.responsavelLogistica ?? 'A definir']]} />
      </Secao>
      <Secao titulo="10. RETIRADA" teste="pdf-retirada-tradein">
        <Campos pares={[['Local do parque', local.texto], ['Prazo para retirada', T.prazoRetirada ? dataBr(T.prazoRetirada) : '—'], ['Agendamento', T.agendamento ? `${dataBr(T.agendamento.slice(0, 10))} às ${T.agendamento.slice(11, 16)}` : '—'], ['Contato responsável pela retirada', T.contatoLocal ? `${T.contatoLocal.nome} · ${T.contatoLocal.telefone} · ${T.contatoLocal.email}` : '—']]} />
      </Secao>
      <Secao titulo="11. CONDIÇÕES DE TRADE-IN — INSPEÇÃO E ACEITE" teste="pdf-inspecao-tradein">
        <p className="folha-nota">{T.condicoesOperacao || '—'}</p>
      </Secao>
      <Secao titulo="12. CONDIÇÕES DE TRADE-IN — RESPONSABILIDADES E PREPARAÇÃO" teste="pdf-responsabilidades-tradein">
        <p className="folha-nota">{T.preparacao || T.responsabilidades || '—'}</p>
        {/* Pacote 4 (bloco 2): recebimento padronizado; versões anteriores saem exatamente como foram emitidas */}
        <Campos pares={recebimentoTradein(v.condicoes).anterior ? [['Recebimento pelo Cliente', `${v.condicoes.forma} · ${v.condicoes.condicao}`]] : [['Recebimento do Cliente', RECEBIMENTO_TRADEIN], ['Prazo de recebimento', recebimentoTradein(v.condicoes).prazo], ['Contados a partir de', recebimentoTradein(v.condicoes).marco]]} />
      </Secao>
      <Secao titulo="13. OBSERVAÇÕES" teste="pdf-observacoes-tradein">
        <p className="folha-nota">{T.observacoes || '—'}</p>
      </Secao>
    </Folha>
  );
}
